import { useCallback, useSyncExternalStore } from 'react';

/**
 * Appearance preference: what the user picked in Settings.
 * `system` defers to the OS. `useTheme` resolves it to a concrete mode so
 * consumers never have to think about the indirection.
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/**
 * Storage key. Must stay in step with the pre-paint bootstrap inlined in
 * `apps/gripp-web/public/index.html`, which reads this key before the bundle
 * loads to avoid a flash of the wrong theme. `themeCss.spec.ts` asserts the
 * two agree.
 */
export const THEME_STORAGE_KEY = 'gripp-theme';

/**
 * What the app resolves to when the user has never picked anything.
 *
 * Deliberately `light`, not `system`. Theme *application* cannot be feature
 * flagged: the pre-paint bootstrap in `index.html` runs before the bundle (and
 * therefore before any flag) is loaded, and `ThemeManager` mounts for everyone
 * including the anonymous flows. If absence resolved to `system`, deploying
 * this would hand the dark palette to every OS-dark user while the APPEARANCE
 * flag is still off and Settings -> Appearance is hidden, with no way out.
 *
 * `system` is honoured only once it has been *chosen* in that flag-gated
 * panel, which keeps the flag a real gate without needing flag state
 * pre-paint. Revisit this default once APPEARANCE is on everywhere.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/**
 * Reads the stored preference, or `undefined` when the user has never chosen
 * one. Never throws: storage can be unavailable or corrupt.
 *
 * The absent case is distinct from any concrete value on purpose. Callers that
 * need something to render with fall back to `DEFAULT_THEME_PREFERENCE`;
 * callers deciding whether there is a choice worth persisting (see
 * `useSetDefaultTheme`) need to tell "never picked" from "picked light".
 */
export const readStoredPreference = (): ThemePreference | undefined => {
  if (!isBrowser()) return undefined;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    const theme = (parsed as { theme?: unknown } | null)?.theme;
    return isPreference(theme) ? theme : undefined;
  } catch {
    return undefined;
  }
};

/** Returns false when storage rejected the write (private mode, quota). */
const writeStoredPreference = (theme: ThemePreference): boolean => {
  if (!isBrowser()) return false;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ theme }));
    return true;
  } catch {
    return false;
  }
};

/**
 * Last-resort in-memory value, used only when storage rejected the write, so
 * the choice still applies for the rest of the session. Deliberately consulted
 * *after* storage, so a successful write always wins and no module state
 * lingers in the normal path.
 */
let unpersistedPreference: ThemePreference | undefined;

const preferenceListeners = new Set<() => void>();

const subscribeToPreference = (onChange: () => void): (() => void) => {
  preferenceListeners.add(onChange);
  return () => {
    preferenceListeners.delete(onChange);
  };
};

const getPreferenceSnapshot = (): ThemePreference =>
  readStoredPreference() ?? unpersistedPreference ?? DEFAULT_THEME_PREFERENCE;

const setStoredPreference = (next: ThemePreference): void => {
  unpersistedPreference = writeStoredPreference(next) ? undefined : next;
  preferenceListeners.forEach((notify) => notify());
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

const subscribeToSystemTheme = (onChange: () => void): (() => void) => {
  if (!isBrowser() || !window.matchMedia) return () => undefined;
  const mql = window.matchMedia(DARK_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
};

const getSystemTheme = (): ResolvedTheme =>
  isBrowser() && window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';

/**
 * Resolved appearance plus a setter.
 *
 * Both halves are read through `useSyncExternalStore`, because both are
 * genuinely external: the OS preference lives behind `matchMedia`, and ours
 * lives in local storage (which the pre-paint bootstrap in `index.html` also
 * reads, before any of this code exists).
 *
 * This deliberately does NOT keep the preference in the react-query cache as
 * `enabled: false` + `initialData` client state. That pattern silently breaks
 * the one consumer that matters: react-query does not notify a *disabled*
 * observer when `setQueryData` runs, so `setQueryData` updated the cache while
 * `ThemeManager` - which sits high in the tree and has no other reason to
 * re-render - kept painting the old mode. The picked theme would then appear
 * only on the next unrelated re-render or a full reload. Verified against
 * react-query v5 in `useTheme.spec.tsx`, which now asserts that an independent
 * subscriber sees the change.
 */
export const useTheme = () => {
  const theme = useSyncExternalStore<ThemePreference>(
    subscribeToPreference,
    getPreferenceSnapshot,
    () => DEFAULT_THEME_PREFERENCE, // server / native snapshot
  );

  const systemTheme = useSyncExternalStore<ResolvedTheme>(
    subscribeToSystemTheme,
    getSystemTheme,
    () => 'light', // server / native snapshot
  );

  const setTheme = useCallback((next: ThemePreference) => {
    setStoredPreference(next);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  return { theme, resolvedTheme, setTheme };
};
